import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, X, Building, Clock, Users, Mail, MessageSquare } from 'lucide-react';
import { companiesApi, PendingInvitation } from '../services/api';
import { AI_PLANS_CHANGED, planApi, type PlanNotification } from '../ai-plans/api';
import {
  enquiriesApi,
  enquiryWorkspaceUrl,
  ENQUIRIES_CHANGED_EVENT,
  NOTIFICATIONS_CHANGED_EVENT,
  type AdviserEnquiryNotification,
  type AdviserLinkNotification,
} from '../advisers/enquiriesApi';

// General notification types
interface BaseNotification {
  id: string;
  type:
    | 'invitation'
    | 'task_assigned'
    | 'strategy_updated'
    | 'general'
    | 'adviser_enquiry'
    | 'adviser_link'
    | 'ai_plan';
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
}

interface InvitationNotification extends BaseNotification {
  type: 'invitation';
  data: PendingInvitation;
}

interface TaskAssignedNotification extends BaseNotification {
  type: 'task_assigned';
  data: {
    taskId: string;
    taskTitle: string;
    assignedBy: string;
  };
}

interface StrategyUpdatedNotification extends BaseNotification {
  type: 'strategy_updated';
  data: {
    strategyId: string;
    strategyName: string;
    updatedBy: string;
  };
}

interface GeneralNotification extends BaseNotification {
  type: 'general';
  data?: any;
}
interface EnquiryNotification extends BaseNotification {
  type: 'adviser_enquiry';
  data: AdviserEnquiryNotification;
}
interface LinkNotification extends BaseNotification {
  type: 'adviser_link';
  data: AdviserLinkNotification;
}
interface AIPlanNotification extends BaseNotification {
  type: 'ai_plan';
  data: PlanNotification;
}

type Notification =
  | InvitationNotification
  | TaskAssignedNotification
  | StrategyUpdatedNotification
  | GeneralNotification
  | EnquiryNotification
  | LinkNotification
  | AIPlanNotification;

export default function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [readError, setReadError] = useState('');

  const fetchNotifications = useCallback(async () => {
    const [invitations, enquiries, links, plans] = await Promise.allSettled([
      companiesApi.getPendingInvitations(),
      enquiriesApi.notifications(),
      enquiriesApi.linkNotifications(),
      planApi.notifications(),
    ]);
    setNotifications((previous) => {
      const invitationNotifications: InvitationNotification[] =
        invitations.status === 'fulfilled' && invitations.value.success && invitations.value.data
          ? invitations.value.data.map((invitation) => ({
              id: `invitation:${invitation.id}`,
              type: 'invitation' as const,
              title: `Invitation to ${invitation.companyName}`,
              message: invitation.inviterName
                ? `${invitation.inviterName} invited you to join as ${invitation.role}`
                : `You've been invited to join as ${invitation.role}`,
              createdAt: invitation.createdAt,
              read: false,
              data: invitation,
            }))
          : previous.filter((item): item is InvitationNotification => item.type === 'invitation');
      const enquiryNotifications: EnquiryNotification[] =
        enquiries.status === 'fulfilled'
          ? enquiries.value.map((enquiry) => ({
              id: `enquiry:${enquiry.id}`,
              type: 'adviser_enquiry',
              title: `Conversation request for ${enquiry.firmName}`,
              message: `${enquiry.name} at ${enquiry.company} · ${enquiry.helpWith}`,
              createdAt: enquiry.createdAt,
              read: Boolean(enquiry.readAt),
              data: enquiry,
            }))
          : previous.filter((item): item is EnquiryNotification => item.type === 'adviser_enquiry');
      const linkNotifications: LinkNotification[] =
        links.status === 'fulfilled'
          ? links.value.map((link) => ({
              id: `link:${link.id}`,
              type: 'adviser_link',
              title: link.title,
              message: link.message,
              createdAt: link.createdAt,
              read: Boolean(link.readAt),
              data: link,
            }))
          : previous.filter((item): item is LinkNotification => item.type === 'adviser_link');
      const planNotifications: AIPlanNotification[] =
        plans.status === 'fulfilled'
          ? plans.value
              .filter((plan) => !plan.resolvedAt || !plan.readAt)
              .map((plan) => ({
                id: `ai-plan:${plan.id}`,
                type: 'ai_plan',
                title: plan.title,
                message: plan.message,
                createdAt: plan.createdAt,
                read: Boolean(plan.readAt),
                data: plan,
              }))
          : previous.filter((item): item is AIPlanNotification => item.type === 'ai_plan');
      return [
        ...invitationNotifications,
        ...enquiryNotifications,
        ...linkNotifications,
        ...planNotifications,
      ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    });
    setLoading(false);
  }, []);

  const openNotification = async (
    notification: EnquiryNotification | LinkNotification | AIPlanNotification
  ) => {
    setIsOpen(false);
    setReadError('');
    if (notification.read) return;
    try {
      if (notification.type === 'adviser_enquiry')
        await enquiriesApi.markNotificationRead(notification.data.id);
      else if (notification.type === 'ai_plan')
        await planApi.markNotificationRead(notification.data.id);
      else await enquiriesApi.markLinkNotificationRead(notification.data.id);
      setNotifications((current) =>
        current.map((item) => (item.id === notification.id ? { ...item, read: true } : item))
      );
    } catch {
      setReadError('Unable to mark the notification as read. You can still review the request.');
    }
  };

  // Handle invitation acceptance
  const handleAcceptInvitation = async (notification: InvitationNotification) => {
    setProcessingId(notification.id);
    try {
      const response = await companiesApi.acceptInvitation(notification.data.id);
      if (response.success) {
        // Remove the accepted notification from the list
        setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
        // Show success message
        alert('Successfully joined the organization!');
        // Refresh the page to update the user's company list
        window.location.reload();
      } else {
        alert(response.error || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert('Failed to accept invitation');
    } finally {
      setProcessingId(null);
    }
  };

  // Handle invitation decline
  const handleDeclineInvitation = (notification: InvitationNotification) => {
    // For now, just remove from UI
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    // TODO: Implement API call to decline invitation
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Check if invitation is expired
  const isInvitationExpired = (invitation: PendingInvitation) => {
    if (!invitation.expiresAt) return false;
    return new Date() > new Date(invitation.expiresAt);
  };

  // Get notification icon
  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'invitation':
        return <Building className="h-5 w-5 text-blue-500 mt-1" />;
      case 'task_assigned':
        return <Users className="h-5 w-5 text-green-500 mt-1" />;
      case 'strategy_updated':
        return <Mail className="h-5 w-5 text-purple-500 mt-1" />;
      case 'adviser_enquiry':
        return <MessageSquare className="h-5 w-5 text-blue-500 mt-1" />;
      case 'adviser_link':
        return <Building className="h-5 w-5 text-blue-500 mt-1" />;
      case 'ai_plan':
        return <Check className="h-5 w-5 text-purple-500 mt-1" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500 mt-1" />;
    }
  };

  // Render notification content based on type
  const renderNotificationContent = (notification: Notification) => {
    if (notification.type === 'invitation') {
      const invitation = notification.data;
      const isExpired = isInvitationExpired(invitation);

      return (
        <div
          className={`p-4 border-b border-gray-100 last:border-b-0 ${
            isExpired ? 'bg-gray-50 opacity-75' : ''
          }`}
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">{getNotificationIcon(notification.type)}</div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900 truncate">{notification.title}</h4>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    invitation.role === 'admin'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {invitation.role}
                </span>
              </div>

              <p className="text-sm text-gray-600 mt-1">{notification.message}</p>

              <div className="flex items-center text-xs text-gray-500 mt-1">
                <Clock className="h-3 w-3 mr-1" />
                <span>
                  {formatDate(notification.createdAt)}
                  {invitation.expiresAt && (
                    <span className={isExpired ? 'text-red-500 ml-1' : 'ml-1'}>
                      • {isExpired ? 'Expired' : `Expires ${formatDate(invitation.expiresAt)}`}
                    </span>
                  )}
                </span>
              </div>

              {!isExpired && (
                <div className="flex space-x-2 mt-3">
                  <button
                    type="button"
                    onClick={() => handleAcceptInvitation(notification)}
                    disabled={processingId === notification.id}
                    className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Check className="h-3 w-3" />
                    <span>{processingId === notification.id ? 'Accepting...' : 'Accept'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDeclineInvitation(notification)}
                    disabled={processingId === notification.id}
                    className="flex items-center space-x-1 px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <X className="h-3 w-3" />
                    <span>Decline</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    const actionUrl =
      notification.type === 'adviser_enquiry'
        ? enquiryWorkspaceUrl(notification.data.practiceId, notification.data.id)
        : (notification.type === 'adviser_link' || notification.type === 'ai_plan') &&
            /^\/(?!\/)/.test(notification.data.actionUrl)
          ? notification.data.actionUrl
          : null;
    return (
      <div
        className={`p-4 border-b border-gray-100 last:border-b-0 ${notification.read ? '' : 'bg-blue-50/50'}`}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">{getNotificationIcon(notification.type)}</div>

          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-gray-900">{notification.title}</h4>
            <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
            <div className="flex items-center text-xs text-gray-500 mt-1">
              <Clock className="h-3 w-3 mr-1" />
              <span>{formatDate(notification.createdAt)}</span>
            </div>
            {actionUrl &&
              (notification.type === 'adviser_enquiry' ||
                notification.type === 'adviser_link' ||
                notification.type === 'ai_plan') && (
                <Link
                  to={actionUrl}
                  onClick={() => void openNotification(notification)}
                  className="inline-block text-sm text-blue-600 hover:text-blue-800 font-medium mt-3"
                >
                  {notification.type === 'adviser_enquiry'
                    ? 'Review enquiry'
                    : notification.type === 'ai_plan'
                      ? 'Open AI plan'
                      : notification.data.type === 'adviser_link_requested'
                        ? 'Review company link'
                        : 'View company'}
                </Link>
              )}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    window.addEventListener(ENQUIRIES_CHANGED_EVENT, fetchNotifications);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, fetchNotifications);
    window.addEventListener(AI_PLANS_CHANGED, fetchNotifications);
    return () => {
      clearInterval(interval);
      window.removeEventListener(ENQUIRIES_CHANGED_EVENT, fetchNotifications);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, fetchNotifications);
      window.removeEventListener(AI_PLANS_CHANGED, fetchNotifications);
    };
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.notification-bell')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isOpen]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="relative notification-bell">
      {/* Bell Button */}
      <button
        type="button"
        aria-label="Notifications"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-32px)] bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <p className="text-sm text-gray-600">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </p>
          </div>
          {readError && (
            <p role="alert" className="p-3 text-sm text-red-700">
              {readError}
            </p>
          )}

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div key={notification.id}>{renderNotificationContent(notification)}</div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 bg-gray-50 text-center">
              <button
                type="button"
                onClick={fetchNotifications}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Refresh
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
